import { AiPill } from '@/components/AiPill';
import { MoneyText } from '@/components/MoneyText';
import { StatusChip } from '@/components/StatusChip';
import { statusLabel } from '@/components/statusLabel';
import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { meetingsApi, type MeetingItem, type MeetingItemVersion } from '@/api/meetings';
import type { Envelope } from '@/api/envelopes';
import { formatCivilDateNear, formatInstant } from '@/i18n/format';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';
import { toneFor } from '@/theme/statusTones';
import { ItemChangeDialog } from './ItemChangeDialog';
import { VersionHistoryDialog } from './VersionHistoryDialog';
import type { ItemAddress } from './itemAddress';
import { sessionPathOf } from './standalone';

interface PersonOption {
  userId: number;
  displayName: string;
}

interface ItemRowProps {
  item: MeetingItem;
  address: ItemAddress | null;
  canWrite: boolean;
  people: PersonOption[];
  currency?: string | null | undefined;
  envelopes?: Envelope[];
  onChanged: () => void;
}

export function ItemRow({
  item,
  address,
  canWrite,
  people,
  currency,
  envelopes = [],
  onChanged,
}: ItemRowProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const navigate = useNavigate();
  const zone = me.user.timezone ?? 'UTC';
  const [changeOpen, setChangeOpen] = useState(false);
  const [versions, setVersions] = useState<MeetingItemVersion[] | null>(null);
  const writable = canWrite && address !== null;
  const envelopeName = envelopes.find((envelope) => envelope.id === item.envelopeId)?.name;

  async function handleConfirm() {
    if (address === null) return;
    await meetingsApi.confirmItem(
      address.virtualAccountId,
      address.meetingId,
      address.occurrenceKey,
      item.id,
    );
    onChanged();
  }

  async function handleHistory() {
    if (address === null) return;
    setVersions(
      await meetingsApi.itemVersions(
        address.virtualAccountId,
        address.meetingId,
        address.occurrenceKey,
        item.id,
      ),
    );
  }

  return (
    <Box sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
      <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography sx={{ flex: 1, whiteSpace: 'pre-wrap' }}>{item.body}</Typography>
        {item.status && <StatusChip domain="meetingItem" status={item.status} />}
        {item.origin === 'AI' && <AiPill />}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {[
          item.ownerUserId === null ? null : personName(item.ownerDisplayName, t),
          item.dueOn ? formatCivilDateNear(item.dueOn, locale, todayCivilInZone(zone)) : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </Typography>
      {item.kind === 'GOAL' && (
        <Stack direction="row" sx={{ gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
          {envelopeName && <Typography variant="caption">{envelopeName}</Typography>}
          {item.targetMinor !== null && currency && (
            <MoneyText minor={item.targetMinor} currency={currency} whole />
          )}
        </Stack>
      )}
      <Stack direction="row" sx={{ gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
        {item.pendingConfirmation && writable && (
          <Button size="small" onClick={() => void handleConfirm()}>
            {t('items.confirm')}
          </Button>
        )}
        {writable && (
          <Button size="small" onClick={() => setChangeOpen(true)}>
            {t('meetings.edit')}
          </Button>
        )}
        {address !== null && (
          <Button size="small" onClick={() => void handleHistory()}>
            {t('items.history')}
          </Button>
        )}
        <Button
          size="small"
          disabled={address === null}
          onClick={() => {
            if (address === null) return;
            void navigate(
              sessionPathOf(address.virtualAccountId, address.meetingId, address.occurrenceKey),
            );
          }}
        >
          {t('items.openInSession')}
        </Button>
        {address === null && (
          <Typography variant="caption" color="text.secondary">
            {t('items.unresolved')}
          </Typography>
        )}
      </Stack>
      {changeOpen && address !== null && (
        <ItemChangeDialog
          item={item}
          address={address}
          people={people}
          {...(currency !== undefined ? { currency } : {})}
          envelopes={envelopes}
          onClose={() => setChangeOpen(false)}
          onSaved={() => {
            setChangeOpen(false);
            onChanged();
          }}
        />
      )}
      {versions !== null && (
        <VersionHistoryDialog
          titleId="item-history"
          rows={versions.map((row) => ({
            key: `${row.version}`,
            version: row.version,
            text: row.body,
            meta: [
              row.actorDisplayName ?? t('items.someone'),
              formatInstant(row.createdAt, locale, zone),
            ].join(' · '),
            reason: row.reason,
            ...(row.status
              ? {
                  tag: {
                    label: statusLabel(t, 'meetingItem', row.status),
                    tone: toneFor('meetingItem', row.status),
                  },
                }
              : {}),
          }))}
          onClose={() => setVersions(null)}
        />
      )}
    </Box>
  );
}
